import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { 
  Building2, 
  Plus, 
  Pencil, 
  Trash2
} from "lucide-react";

export default function EmpresasPage() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [formData, setFormData] = useState({ nome_empresa: "" });

  useEffect(() => {
    fetchEmpresas();
  }, []);

  const fetchEmpresas = async () => {
    try {
      const response = await axios.get(`${API}/empresas`);
      setEmpresas(response.data);
    } catch (error) {
      toast.error("Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmpresa) {
        await axios.put(`${API}/empresas/${editingEmpresa.id}`, formData);
        toast.success("Empresa atualizada");
      } else {
        await axios.post(`${API}/empresas`, formData);
        toast.success("Empresa criada");
      }
      setDialogOpen(false);
      setEditingEmpresa(null);
      setFormData({ nome_empresa: "" });
      fetchEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar empresa");
    }
  };

  const handleEdit = (empresa) => {
    setEditingEmpresa(empresa);
    setFormData({ nome_empresa: empresa.nome_empresa });
    setDialogOpen(true);
  };

  const handleDelete = async (empresaId) => {
    if (!confirm("Tem a certeza que deseja eliminar esta empresa? Todas as viaturas associadas serão eliminadas.")) return;
    try {
      await axios.delete(`${API}/empresas/${empresaId}`);
      toast.success("Empresa eliminada");
      fetchEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar empresa");
    }
  };

  const headerAction = (
    <Dialog open={dialogOpen} onOpenChange={(open) => {
      setDialogOpen(open);
      if (!open) {
        setEditingEmpresa(null);
        setFormData({ nome_empresa: "" });
      }
    }}>
      <DialogTrigger asChild>
        <Button className="bg-[#0047FF] hover:bg-[#0035CC]" data-testid="add-empresa-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nova Empresa
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingEmpresa ? "Editar Empresa" : "Nova Empresa"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome da Empresa</Label>
            <Input
              id="nome"
              value={formData.nome_empresa}
              onChange={(e) => setFormData({ ...formData, nome_empresa: e.target.value })}
              placeholder="Ex: Empresa XYZ, Lda."
              required
              data-testid="empresa-nome-input"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-[#0047FF] hover:bg-[#0035CC]" data-testid="empresa-submit-btn">
              {editingEmpresa ? "Guardar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <PageLayout title="Empresas" subtitle="Gestão de empresas clientes" headerAction={headerAction}>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
        </div>
      ) : empresas.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-8 text-center">
            <Building2 className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#64748B] mb-4">Nenhuma empresa registada</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#0047FF] hover:bg-[#0035CC]">
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeira Empresa
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E2E8F0]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome da Empresa</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {empresas.map((empresa) => (
                <TableRow key={empresa.id} data-testid={`empresa-row-${empresa.id}`}>
                  <TableCell className="font-medium">{empresa.nome_empresa}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(empresa)} data-testid={`edit-empresa-${empresa.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-[#D50000]" onClick={() => handleDelete(empresa.id)} data-testid={`delete-empresa-${empresa.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </PageLayout>
  );
}
